/**
 * Admin → Auth → JWT Verifiers (Phase A, doc 21 §2.1).
 *
 * Per-tenant registry of trusted JWT issuers. Each row defines a JWKS
 * URL Discovery will accept session-bearing JWTs from, beyond
 * Discovery's own HS256 local signer.
 *
 * Path A bootstrap auto-seeds the FOCTTA Core row when a tenant is
 * provisioned from Core. This page lets the IT Admin add their own
 * Okta / Azure AD / Auth0 / external IdP JWKS URLs so those tokens
 * are also accepted at /v1/auth/exchange.
 *
 * UX deliberately minimal - single page, table + add-form. The full
 * SAML/OIDC IdP wiring lives at /admin/auth/idp (P3.1); this page is
 * for the lower-level "we accept JWTs signed by these JWKS endpoints"
 * surface that the cross-app bridge consumes.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  KeyRound,
  Loader2,
  Plus,
  RefreshCw,
  ShieldOff,
  Trash2,
} from 'lucide-react';

import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PageContainer, PageHeader } from '@/components/viz/PageHeader';
import {
  createJwtVerifier,
  deleteJwtVerifier,
  listJwtVerifiers,
  setVerifierEnabled,
  type JwtVerifier,
} from '@/lib/jwt-verifiers-api';

export function JwtVerifiers(): JSX.Element {
  const [rows, setRows] = useState<JwtVerifier[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);

  // Form state
  const [label, setLabel] = useState('');
  const [issuer, setIssuer] = useState('');
  const [jwksUrl, setJwksUrl] = useState('');
  const [audience, setAudience] = useState('foctta-discovery');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await listJwtVerifiers();
      setRows(list);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const resetForm = (): void => {
    setLabel('');
    setIssuer('');
    setJwksUrl('');
    setAudience('foctta-discovery');
    setSubmitError(null);
  };

  const onSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setSubmitError(null);
    if (!label.trim() || !issuer.trim() || !jwksUrl.trim() || !audience.trim()) {
      setSubmitError('All fields are required.');
      return;
    }
    setSubmitting(true);
    try {
      await createJwtVerifier({
        kind: 'jwks',
        label: label.trim(),
        issuer: issuer.trim(),
        config: {
          jwksUrl: jwksUrl.trim(),
          audience: audience.trim(),
        },
      });
      resetForm();
      setShowAdd(false);
      await refresh();
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  const onToggle = async (row: JwtVerifier): Promise<void> => {
    setBusy(row.id);
    try {
      await setVerifierEnabled(row.id, !row.enabled);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  };

  const onDelete = async (row: JwtVerifier): Promise<void> => {
    const ok = window.confirm(
      `Delete verifier "${row.label}" (${row.issuer})?\n\nBridge JWTs from this issuer will no longer be accepted at /v1/auth/exchange.`,
    );
    if (!ok) return;
    setBusy(row.id);
    try {
      await deleteJwtVerifier(row.id);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  };

  return (
    <PageContainer>
      <PageHeader
        title="JWT Verifiers"
        subtitle="Trusted issuers whose signed JWTs Discovery will accept at /v1/auth/exchange (FOCTTA Core bridge + customer IdPs)."
        actions={
          <>
            <Button variant="ghost" size="sm" onClick={() => void refresh()} disabled={loading}>
              <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button size="sm" onClick={() => setShowAdd((s) => !s)}>
              <Plus className="h-4 w-4 mr-1" />
              {showAdd ? 'Cancel' : 'Add verifier'}
            </Button>
          </>
        }
      />

      {error ? (
        <GlassCard className="border-red-500/40">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-500">
              <AlertTriangle className="h-4 w-4" />
              {error}
            </CardTitle>
          </CardHeader>
        </GlassCard>
      ) : null}

      {showAdd ? (
        <GlassCard className="mt-4">
          <CardHeader>
            <CardTitle>Add JWKS verifier</CardTitle>
            <CardDescription clamp>
              Discovery accepts session JWTs signed by any registered JWKS URL. Used for the FOCTTA
              Core cross-app bridge and for direct customer-IdP sign-in.
            </CardDescription>
          </CardHeader>
          <form onSubmit={(e) => void onSubmit(e)} className="px-6 pb-6 space-y-3">
            <div>
              <label className="text-sm font-medium">Label</label>
              <input
                type="text"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder='e.g. "Acme Okta" or "FOCTTA Core"'
                className="mt-1 w-full rounded-md border bg-background px-3 py-2 text-sm"
                disabled={submitting}
              />
            </div>
            <div>
              <label className="text-sm font-medium">Issuer (iss claim)</label>
              <input
                type="text"
                value={issuer}
                onChange={(e) => setIssuer(e.target.value)}
                placeholder="https://acme.okta.com or https://app.foctta.com"
                className="mt-1 w-full rounded-md border bg-background px-3 py-2 text-sm font-mono"
                disabled={submitting}
              />
            </div>
            <div>
              <label className="text-sm font-medium">JWKS URL</label>
              <input
                type="text"
                value={jwksUrl}
                onChange={(e) => setJwksUrl(e.target.value)}
                placeholder="https://acme.okta.com/oauth2/default/v1/keys"
                className="mt-1 w-full rounded-md border bg-background px-3 py-2 text-sm font-mono"
                disabled={submitting}
              />
            </div>
            <div>
              <label className="text-sm font-medium">Expected audience (aud claim)</label>
              <input
                type="text"
                value={audience}
                onChange={(e) => setAudience(e.target.value)}
                className="mt-1 w-full rounded-md border bg-background px-3 py-2 text-sm font-mono"
                disabled={submitting}
              />
              <div className="mt-1 text-xs text-muted-foreground">
                Defaults to <code className="font-mono">foctta-discovery</code> - matches what Core's
                bridge endpoint mints. Customer IdPs typically use their own application/client id.
              </div>
            </div>
            {submitError ? (
              <div className="text-sm text-red-500 flex items-center gap-2">
                <AlertTriangle className="h-4 w-4" />
                {submitError}
              </div>
            ) : null}
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="ghost" onClick={() => setShowAdd(false)} disabled={submitting}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} data-action="disc.adm_jwt.a01">
                {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                Add
              </Button>
            </div>
          </form>
        </GlassCard>
      ) : null}

      <GlassCard className="mt-4">
        <CardHeader>
          <CardTitle>Registered verifiers ({rows.length})</CardTitle>
          <CardDescription>
            Rows with <span className="font-mono">isSystem=true</span> were auto-registered by
            Path A bootstrap (FOCTTA Core). All rows can be disabled or removed.
          </CardDescription>
        </CardHeader>
        <div className="px-6 pb-6">
          {loading ? (
            <div className="text-sm text-muted-foreground flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading…
            </div>
          ) : rows.length === 0 ? (
            <div className="text-sm text-muted-foreground py-6 text-center">
              No verifiers registered yet. Click <strong>Add verifier</strong> above to register your
              first one - typically your Okta / Entra / Auth0 JWKS endpoint.
            </div>
          ) : (
            <ul className="space-y-3">
              {rows.map((row) => (
                <li
                  key={row.id}
                  className="rounded-md border bg-background/60 p-4 flex items-start gap-3"
                >
                  <KeyRound className="h-5 w-5 mt-0.5 text-muted-foreground" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium">{row.label}</span>
                      <Badge variant={row.enabled ? 'default' : 'secondary'}>
                        {row.enabled ? 'enabled' : 'disabled'}
                      </Badge>
                      {row.isSystem ? <Badge variant="outline">system</Badge> : null}
                      <Badge variant="outline">{row.kind}</Badge>
                    </div>
                    <div className="mt-1 text-xs text-muted-foreground font-mono break-all">
                      iss: {row.issuer}
                    </div>
                    {row.config.jwksUrl ? (
                      <div className="mt-0.5 text-xs text-muted-foreground font-mono break-all">
                        jwks: {row.config.jwksUrl}
                      </div>
                    ) : null}
                    {row.config.audience ? (
                      <div className="mt-0.5 text-xs text-muted-foreground font-mono">
                        aud: {row.config.audience}
                      </div>
                    ) : null}
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => void onToggle(row)}
                      disabled={busy === row.id}
                      data-action="disc.adm_jwt.a02"
                    >
                      {row.enabled ? (
                        <>
                          <ShieldOff className="h-4 w-4 mr-1" /> Disable
                        </>
                      ) : (
                        <>
                          <CheckCircle2 className="h-4 w-4 mr-1" /> Enable
                        </>
                      )}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => void onDelete(row)}
                      disabled={busy === row.id}
                      data-action="disc.adm_jwt.a03"
                    >
                      <Trash2 className="h-4 w-4 mr-1" /> Remove
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </GlassCard>
    </PageContainer>
  );
}
